/**
 * Signature images in device draft backups, stored once.
 *
 * A clinical form's snapshot used to carry every signature image inline, and
 * snapshots are never evicted while they may hold unsaved work. On one browser
 * the images were 69% of a 2.9 MB store (the browser limit is ~5 MB). Each
 * large data URL is now written once under a key derived from its content and
 * replaced in the snapshot by a small reference; reading a snapshot puts the
 * images back, so what can be recovered is unchanged.
 */

const IMAGE_PREFIX = 'evv_draft_image:';
const REF_KEY = '__draftImageRef';
// Below this a reference saves nothing worth the extra key.
const MIN_INLINE_BYTES = 2048;

const isLargeDataUrl = (value) => typeof value === 'string'
  && value.length >= MIN_INLINE_BYTES && value.startsWith('data:');

// FNV-1a, 32-bit. Combined with the length it keys identical images together;
// a collision is detected on write (stored content compared) and the image
// then stays inline, so a reference can never resolve to the wrong image.
// The backup is written on every keystroke with the same signature strings,
// so remember recent keys instead of re-hashing ~100 KB each time.
const recentKeys = new Map();
const RECENT_KEY_LIMIT = 32;

function contentKey(value) {
  const cached = recentKeys.get(value);
  if (cached) return cached;
  const key = hashKey(value);
  if (recentKeys.size >= RECENT_KEY_LIMIT) recentKeys.delete(recentKeys.keys().next().value);
  recentKeys.set(value, key);
  return key;
}

function hashKey(value) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${IMAGE_PREFIX}${value.length}:${hash.toString(16)}`;
}

function mapStrings(value, fn) {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map((item) => mapStrings(item, fn));
  if (value && typeof value === 'object') {
    if (typeof value[REF_KEY] === 'string') return fn(value);
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapStrings(v, fn)]));
  }
  return value;
}

/**
 * Replace large data URLs with references, writing each image once.
 * @param {object} formData
 * @param {Storage} storage
 * @returns {object} formData safe to serialise into the snapshot
 */
export function storeSnapshotImages(formData, storage) {
  return mapStrings(formData, (value) => {
    if (!isLargeDataUrl(value)) return value;
    const key = contentKey(value);
    try {
      const existing = storage.getItem(key);
      if (existing === null) {
        storage.setItem(key, value);
        if (storage.getItem(key) !== value) return value; // write refused: stay inline
      } else if (existing !== value) {
        return value; // key collision: stay inline
      }
      return { [REF_KEY]: key };
    } catch {
      return value;
    }
  });
}

/**
 * Put stored images back. A reference whose image is missing becomes an empty
 * string, so the rest of the draft still restores (a signature can be redone).
 * @param {object} formData
 * @param {Storage} storage
 */
export function restoreSnapshotImages(formData, storage) {
  return mapStrings(formData, (value) => {
    if (!value || typeof value !== 'object' || typeof value[REF_KEY] !== 'string') return value;
    try {
      return storage.getItem(value[REF_KEY]) ?? '';
    } catch {
      return '';
    }
  });
}

const referencedKeys = (formData, out) => {
  mapStrings(formData, (value) => {
    if (value && typeof value === 'object' && typeof value[REF_KEY] === 'string') out.add(value[REF_KEY]);
    return value;
  });
  return out;
};

/** True when a snapshot's form still carries a large image inline. */
export function hasInlineImages(formData) {
  let found = false;
  mapStrings(formData, (value) => {
    if (!found && isLargeDataUrl(value)) found = true;
    return value;
  });
  return found;
}

/**
 * Delete stored images that no snapshot refers to. Only images are removed;
 * snapshots themselves are never touched here.
 * @param {Storage} storage
 * @param {(key: string) => boolean} isSnapshotKey
 */
export function removeUnreferencedSnapshotImages(storage, isSnapshotKey) {
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
    const used = new Set();
    for (const key of keys) {
      if (!key || !isSnapshotKey(key)) continue;
      try {
        referencedKeys(JSON.parse(storage.getItem(key) || '{}').formData, used);
      } catch {
        // An unreadable snapshot may still reference images: keep them all.
        return;
      }
    }
    for (const key of keys) {
      if (key && key.startsWith(IMAGE_PREFIX) && !used.has(key)) storage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing to clean.
  }
}
